import { useEffect, useMemo, useState } from "react";
import { Get } from "api";
import { useUserPermissions } from "./useUserPermissions";

// One of our own apps a user can come from. Same payload as the manage dashboard's
// /manage/own-apps, so both dashboards read the same shape. The legacy app ids the
// endpoint appends carry only an id and a name, so the rest is optional.
export interface SourceApp {
  appId: string;
  name: string;
  isWeb?: boolean;
  platformIds?: number[];
}

export interface SourceAppOption {
  label: string;
  value: string;
}

// Backend platform ids (pkg/platform.go): 1 = Android, 2 = iOS.
const PLATFORM_NAMES: Record<number, string> = { 1: "Android", 2: "iOS" };

// The list is the same for every admin and never changes within a session, so the three
// analytics screens share one request.
let pending: Promise<SourceApp[]> | null = null;

const fetchSourceApps = (): Promise<SourceApp[]> => {
  if (!pending) {
    pending = Get("/dashboard/options/source-app")
      .then((response: unknown) => (Array.isArray(response) ? response : []))
      .catch(() => {
        pending = null;

        return [];
      });
  }

  return pending;
};

export const useSourceApps = () => {
  const { isMain } = useUserPermissions();
  const [sourceApps, setSourceApps] = useState<SourceApp[]>([]);

  useEffect(() => {
    if (!isMain) {
      return;
    }

    let isActive = true;

    fetchSourceApps().then((apps) => {
      if (isActive) {
        setSourceApps(apps);
      }
    });

    return () => {
      isActive = false;
    };
  }, [isMain]);

  // Two pairs of our apps carry the same name on both platforms (TesterMAX, SUPREME KING).
  // Left alone they turn into two identical rows in the filter and two identical cells in
  // the breakdown column, so a shared name gets its platform.
  const namesByAppId = useMemo(() => {
    const timesUsed: Record<string, number> = {};
    sourceApps.forEach((app) => {
      timesUsed[app.name] = (timesUsed[app.name] ?? 0) + 1;
    });

    const names: Record<string, string> = {};
    sourceApps.forEach((app) => {
      const platforms = (app.platformIds ?? [])
        .map((platformId) => PLATFORM_NAMES[platformId])
        .filter(Boolean)
        .join(" / ");

      names[app.appId] =
        timesUsed[app.name] > 1 && platforms
          ? `${app.name} (${platforms})`
          : app.name;
    });

    return names;
  }, [sourceApps]);

  const sourceAppOptions: SourceAppOption[] = useMemo(
    () =>
      sourceApps.map((app) => ({
        label: namesByAppId[app.appId] ?? app.name,
        value: app.appId,
      })),
    [sourceApps, namesByAppId]
  );

  return { sourceApps, sourceAppOptions, namesByAppId };
};
